<template lang="html">
  <div class="product-gallery relative">
    <ClientOnly>
      <template #fallback>
        <div class="hidden" />
      </template>
      <LightgalleryContainer
        v-if="needShowGallery"
        key="gallery"
        :onInit="handleInitGallery"
      >
        <a v-for="img in slides" :key="img.id" :data-src="img.src" />
      </LightgalleryContainer>
    </ClientOnly>

    <SwiperContainer loop :slides-per-view="1" class="max-sm:h-120 sm:aspect-4/5" @swiper="handleSwiperInited">
      <SwiperSlide v-for="(img, index) in slides" :key="img.id">
        <!-- Первый слайд — LCP страницы товара: высокий приоритет; скрытые
        слайды свайпера догружаются лениво -->
        <UiPicture
          :src="img.id"
          variant="imageFull"
          :alt="galleryAlt(index)"
          sizes="100vw sm:50vw"
          :loading="index === 0 ? undefined : 'lazy'"
          :fetchpriority="index === 0 ? 'high' : undefined"
          @click="openGallery()"
        />
      </SwiperSlide>

      <!-- Видео идут после фото: первое изображение остаётся главным для
      листингов, OG-картинки и фидов, и подменять его роликом нельзя -->
      <SwiperSlide v-for="video in videos" :key="video.id">
        <video
          class="h-full w-full bg-black object-contain"
          controls
          playsinline
          preload="metadata"
          :poster="video.posterUrl"
          :aria-label="videoAlt(video)"
        >
          <source :src="video.src" type="video/mp4">
        </video>
      </SwiperSlide>
    </SwiperContainer>

    <div
      v-if="totalSlides > 1"
      key="prevSlide"
      class="product-gallery__nav sm:-left- absolute top-1/2 left-0 z-10 -translate-y-1/2"
    >
      <UButton
        square
        color="neutral"
        variant="soft"
        class="border border-default"
        icon="i-lucide-chevron-left"
        @click="$slider?.slidePrev()"
      />
    </div>
    <div
      v-if="totalSlides > 1"
      key="newSlide"
      class="product-gallery__nav absolute top-1/2 right-0 z-10 -translate-y-1/2"
    >
      <UButton
        square
        color="neutral"
        variant="soft"
        class="border border-default"
        icon="i-lucide-chevron-right"
        @click="$slider?.slideNext()"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { PromiseController } from '@repo/alt/common/Utils/Async.js'
import type { ProductDto } from '@repo/api/web/models'
import type { LightGallery } from 'lightgallery/lightgallery'
import type Swiper from 'swiper'
import { Swiper as SwiperContainer, SwiperSlide } from 'swiper/vue'
import 'lightgallery/css/lightgallery.css'
import 'swiper/swiper-bundle.css'

import UiPicture from '~/app/components/Image/UiPicture.vue'

const LightgalleryContainer = defineAsyncComponent(
  () => import('lightgallery/vue'),
)

const props = withDefaults(
  defineProps<{
    value: ProductDto
  }>(),
  {},
)

const initGalleryPromise = new PromiseController()
const { support } = useResources()
const $slider = ref<Swiper>()
const $gallery = ref<LightGallery>()
const needShowGallery = ref(false)

const slides = computed(() => {
  const images = props.value.images ?? []
  const items = images.map(img => ({
    id: img.id,
    src: support.getResourceUrl(img.id!),
  }))

  // Нет фото у товара — показываем плаг-заглушку, иначе галерея пустая.
  // UiPicture сам подставляет /Plug.svg, когда src (id ресурса) falsy.
  if (items.length === 0) {
    return [{ id: undefined, src: '/Plug.svg' }]
  }

  return items
})

/**
 * Видео товара. Ролик проигрывается прямо в слайдере, в lightgallery не попадает:
 * галерея остаётся про фото, а плеер платформы туда тянуть незачем
 */
const videos = computed(() =>
  (props.value.videos ?? []).map(video => ({
    displayName: video.displayName,
    id: video.id,
    // Постер отдаётся мгновенно как обычная картинка, тогда как первый кадр
    // самого ролика появится только после подтягивания заголовка файла
    posterUrl: video.posterResourceId
      ? support.getResourceUrl(video.posterResourceId)
      : undefined,
    src: support.getResourceUrl(video.id),
  })),
)

/** Стрелки нужны, когда слайд не один — считаем и фото, и видео */
const totalSlides = computed(() => slides.value.length + videos.value.length)

function videoAlt(video: { displayName?: string | null }) {
  const name = props.value.wellKnownAttributes?.Name ?? ''
  return video.displayName?.trim() || `${name} — видео`
}

function galleryAlt(index: number) {
  const name = props.value.wellKnownAttributes?.Name ?? ''

  return index === 0 ? name : `${name} — фото ${index + 1}`
}

function handleSwiperInited(swiper: any) {
  $slider.value = swiper
}
async function openGallery() {
  needShowGallery.value = true

  if (!$gallery.value) {
    await initGalleryPromise.promise
  }

  $gallery.value?.openGallery()
}
function handleInitGallery(gallery: { instance: LightGallery }) {
  $gallery.value = gallery.instance
  initGalleryPromise.resolve($gallery.value)
}
</script>
